// Syft ACP - Lib <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import type { ClipboardEvent } from 'react'

import type { CountryCode } from 'syft-acp-core/lib/i18n'

/**
 * Region suffix the data warehouse appends to IDs, keyed by the internal
 * country code. Note the UK is represented internally as the `'GB'` country
 * code, but the data-warehouse suffix is `'UK'`.
 */
export const REGION_ID_SUFFIX: Record<CountryCode, RegionSuffix> = {
  GB: 'UK',
  US: 'US',
}

export type RegionSuffix = 'UK' | 'US'

export type RegionIdResult = {
  /** Digits-only ID with thousands separators and any region suffix removed. */
  value: string
  /** The region suffix found on the pasted value, or `null` if there was none. */
  detectedSuffix: RegionSuffix | null
  /** True when a suffix was present and it does not match the current region. */
  regionMismatch: boolean
  /** True when `value` differs from the raw input, i.e. we normalised something. */
  changed: boolean
}

export type RegionIdWarning = {
  title: string
  message: string
}

// A run of digits, thousands-separator commas and inner spaces, optionally
// followed by a US/UK data-warehouse suffix. Anything else (letters, symbols,
// UUIDs, …) fails to match and is left untouched.
const REGION_ID_RE = /^([\d,\s]+?)\s*(UK|US)?$/i

/**
 * Parses a value pasted into an ID field. Recognises data-warehouse formatted
 * IDs such as `1,234UK` or `1,234,456US`, returning the bare numeric ID and
 * flagging whether the region suffix matches the ACP region we are viewing.
 *
 * Values that are not purely numeric (with optional thousands separators and a
 * region suffix) are returned unchanged so free-text pastes are never mangled.
 */
export function parseRegionId(raw: string, region: CountryCode): RegionIdResult {
  const unchanged: RegionIdResult = {
    value: raw,
    detectedSuffix: null,
    regionMismatch: false,
    changed: false,
  }

  const match = (raw ?? '').trim().match(REGION_ID_RE)
  if (!match) return unchanged

  const digits = match[1].replace(/\D/g, '')
  if (!digits) return unchanged

  const detectedSuffix = (match[2]?.toUpperCase() as RegionSuffix | undefined) ?? null
  // Only flag a mismatch when we actually know the current region; if it is
  // unknown we still strip the value but stay silent rather than mis-warn.
  const expectedSuffix = REGION_ID_SUFFIX[region] as RegionSuffix | undefined
  const regionMismatch =
    detectedSuffix !== null && expectedSuffix !== undefined && detectedSuffix !== expectedSuffix

  return {
    value: digits,
    detectedSuffix,
    regionMismatch,
    changed: digits !== raw,
  }
}

/** Builds the wrong-region warning for a parsed result, or `null` if there is no mismatch. */
export function regionIdWarning(result: RegionIdResult, region: CountryCode): RegionIdWarning | null {
  if (!result.regionMismatch || !result.detectedSuffix) return null

  return {
    title: 'Possible wrong-region ID',
    message:
      `You pasted a ${result.detectedSuffix} ID while working in ${REGION_ID_SUFFIX[region]}. ` +
      `Filled in "${result.value}" — double-check this is the right record.`,
  }
}

export type HandleRegionIdPasteOptions = {
  region: CountryCode
  /** Applies the resulting field value (e.g. final-form's `input.onChange`). */
  applyValue: (value: string) => void
  /** Surfaces a wrong-region warning (e.g. a toast). */
  warn: (warning: RegionIdWarning) => void
}

/**
 * Paste handler for ID inputs. When the pasted text is a recognised
 * data-warehouse ID it normalises the value (stripping thousands separators and
 * the US/UK suffix) into the field and, on a region mismatch, raises a warning.
 * Unrecognised pastes fall through to the browser's default behaviour.
 */
export function handleRegionIdPaste(
  event: ClipboardEvent<HTMLInputElement>,
  { region, applyValue, warn }: HandleRegionIdPasteOptions,
): void {
  const pasted = event.clipboardData?.getData('text') ?? ''
  const result = parseRegionId(pasted, region)

  // Nothing to normalise and no region problem: let the browser paste as usual.
  if (!result.changed && !result.regionMismatch) return

  const input = event.currentTarget
  const start = input.selectionStart ?? input.value.length
  const end = input.selectionEnd ?? input.value.length
  const nextValue = input.value.slice(0, start) + result.value + input.value.slice(end)

  event.preventDefault()
  applyValue(nextValue)

  const warning = regionIdWarning(result, region)
  if (warning) warn(warning)
}
